<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  services: { type: Array, default: () => [] },
  selectedServiceId: { type: [String, Number], default: null },
});

const emit = defineEmits(['select']);
const { t } = useI18n();
const emptyValue = t('SALES.DETAIL.NUCLEO.EMPTY_VALUE');
const statusLabel = status =>
  ({
    draft: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.DRAFT'),
    quoted: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.QUOTED'),
    approved: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.APPROVED'),
    scheduled: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.SCHEDULED'),
    in_progress: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.IN_PROGRESS'),
    completed: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.COMPLETED'),
    accepted: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.ACCEPTED'),
    disputed: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.DISPUTED'),
    canceled: t('NUCLEO_WORK_ORDERS.DETAIL.STATUS.CANCELED'),
  })[status] ||
  status ||
  emptyValue;
const statusClass = status => {
  if (status === 'approved') return 'bg-lais-primary-50 text-lais-primary';
  if (['completed', 'accepted'].includes(status))
    return 'bg-green-50 text-green-700';
  if (status === 'canceled') return 'bg-red-50 text-red-700';

  return 'bg-lais-bg-soft text-lais-text-body';
};
const formatDate = value =>
  value ? new Intl.DateTimeFormat('pt-BR').format(new Date(value)) : emptyValue;
</script>

<template>
  <section
    data-test="sales-detail-nucleo-services"
    class="overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
  >
    <div
      class="hidden grid-cols-[minmax(13rem,1fr)_minmax(10rem,0.55fr)_minmax(10rem,0.45fr)] gap-4 bg-lais-bg-soft px-5 py-3 text-xs font-semibold uppercase text-lais-text-muted sm:grid"
    >
      <span>{{ t('SALES.DETAIL.NUCLEO.SERVICE') }}</span>
      <span>{{ t('SALES.DETAIL.NUCLEO.STATUS') }}</span>
      <span>{{ t('SALES.DETAIL.NUCLEO.DELIVERY_FORECAST') }}</span>
    </div>
    <div class="divide-y divide-lais-border-soft">
      <button
        v-for="service in services"
        :key="service.id"
        type="button"
        class="grid w-full gap-2 px-5 py-4 text-left text-sm hover:bg-lais-primary-50 sm:grid-cols-[minmax(13rem,1fr)_minmax(10rem,0.55fr)_minmax(10rem,0.45fr)] sm:items-center sm:gap-4"
        :aria-pressed="String(service.id) === String(selectedServiceId)"
        @click="emit('select', service.id)"
      >
        <span class="flex flex-wrap gap-2">
          <span class="text-xs text-lais-text-muted sm:hidden">
            {{ t('SALES.DETAIL.NUCLEO.SERVICE') }}
          </span>
          <span
            v-if="service.tipo_servico?.name"
            class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-xs font-semibold text-lais-text-body"
          >
            {{ service.tipo_servico.name }}
          </span>
          <span
            v-if="service.servico_detalhado?.name"
            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-xs font-semibold text-lais-primary"
          >
            {{ service.servico_detalhado.name }}
          </span>
          <span
            v-if="
              !service.tipo_servico?.name && !service.servico_detalhado?.name
            "
            class="text-lais-text-body"
          >
            {{ service.nome || emptyValue }}
          </span>
        </span>
        <span class="flex flex-wrap items-center gap-2">
          <span class="text-xs text-lais-text-muted sm:hidden">
            {{ t('SALES.DETAIL.NUCLEO.STATUS') }}
          </span>
          <span
            class="rounded-full px-2.5 py-1 text-xs font-semibold"
            :class="statusClass(service.status)"
          >
            {{ statusLabel(service.status) }}
          </span>
        </span>
        <span class="text-lais-text-body">
          <span class="mr-2 text-xs text-lais-text-muted sm:hidden">
            {{ t('SALES.DETAIL.NUCLEO.DELIVERY_FORECAST') }}
          </span>
          {{ formatDate(service.data_previsao) }}
        </span>
      </button>
    </div>
  </section>
</template>
